import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { companyInfo } from '../data/tourData';
import './TermsConditions.css';

const TermsConditions = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions - NomadTrailz</title>
        <meta name="description" content="Read our terms and conditions for booking tours with NomadTrailz." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="policy-hero">
          <div className="hero-background">
            <img 
              src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt="Mountain Landscape" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Terms & Conditions
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Please read these terms carefully before booking your adventure
            </motion.p>
          </div>
        </section>

        {/* Terms Content */}
        <section className="policy-content">
          <div className="container">
            <motion.div 
              className="content-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="policy-section">
                <p>
                  By using NomadTrailz's services, you agree to be bound by these terms and conditions. 
                  Please read them carefully before making any bookings or using our website.
                </p>
              </div>

              <div className="policy-section">
                <h2>1. Acceptance of Terms</h2>
                <p>
                  By accessing and using NomadTrailz's website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
                <p>
                  Use of our website or booking via WhatsApp, email, or social media also implies acceptance.
                </p>
              </div>

              <div className="policy-section">
                <h2>2. Booking and Payment Terms</h2>
                <ul>
                  <li>All bookings are subject to availability and confirmation.</li>
                  <li>A minimum deposit of 25% of the total package cost is required to confirm your booking.</li>
                  <li>The remaining balance must be paid at least 15 days before the travel date.</li>
                  <li>We accept payments through various modes including online transfers, UPI, and digital wallets.</li>
                  <li>All prices are quoted in Indian Rupees (INR) and are inclusive of applicable taxes unless otherwise specified.</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>3. Travel Documents and Requirements</h2>
                <ul>
                  <li>It is the responsibility of the traveler to ensure they have valid travel documents including ID proof, permits, and any required vaccinations.</li>
                  <li>For international tours, valid passports and visas are mandatory.</li>
                  <li>NomadTrailz is not responsible for any issues arising from invalid or missing travel documents.</li>
                  <li>Travelers must comply with all local laws and regulations of the destination.</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>4. Health and Safety</h2>
                <ul>
                  <li>Travelers must inform us of any medical conditions or special requirements at the time of booking.</li>
                  <li>NomadTrailz does not provide travel or medical insurance. We strongly recommend travelers arrange their own insurance to cover medical emergencies, accidents, and trip cancellations.</li>
                  <li>Adventure travel involves inherent risks. Participants voluntarily assume all such risks.</li>
                  <li>NomadTrailz reserves the right to refuse service to anyone who may pose a risk to themselves or others.</li>
                  <li>During treks and adventure activities, participants must follow all safety instructions provided by our guides.</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>5. Cancellation and Refund Policy</h2>
                <ul>
                  <li>Cancellations must be communicated in writing via email or phone.</li>
                  <li>Cancellation charges apply as per our cancellation policy.</li>
                  <li>No refunds will be provided for no-shows or early departures.</li>
                  <li>Force majeure events may result in tour modifications or cancellations with appropriate refunds.</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>6. Tour Modifications</h2>
                <ul>
                  <li>NomadTrailz reserves the right to modify tour itineraries due to weather conditions, government restrictions, or other unforeseen circumstances.</li>
                  <li>We will make reasonable efforts to inform participants in advance.</li>
                  <li>We will provide suitable alternatives whenever possible.</li>
                  <li>No compensation will be provided for minor itinerary changes that do not significantly affect the tour experience.</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>7. Liability and Responsibility</h2>
                <ul>
                  <li>NomadTrailz acts as an intermediary between travelers and service providers.</li>
                  <li>We are not liable for any loss, damage, or injury during the tour.</li>
                  <li>NomadTrailz, its employees, or representatives shall not be held responsible for delays, accidents, natural calamities, or acts beyond control.</li>
                  <li>Travelers are responsible for their personal belongings and safety.</li>
                  <li>We recommend purchasing comprehensive travel insurance.</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>8. Privacy and Data Protection</h2>
                <p>
                  Your privacy is important to us. Please refer to our Privacy Policy for details on how we collect, use, and protect your personal information.
                </p>
              </div>

              <div className="policy-section">
                <h2>9. Intellectual Property</h2>
                <p>
                  All content on this website, including text, images, and logos, is the property of NomadTrailz and is protected by copyright laws. Unauthorized use is prohibited.
                </p>
              </div>

              <div className="policy-section">
                <h2>10. Governing Law</h2>
                <p>
                  These terms and conditions are governed by the laws of India. Any disputes will be subject to the exclusive jurisdiction of courts in New Delhi.
                </p>
              </div>

              <div className="policy-section">
                <h2>11. Contact Information</h2>
                <p>
                  For any questions regarding these terms and conditions, please contact us:
                </p>
                <ul>
                  <li><strong>Phone:</strong> +91-7678501921</li>
                  <li><strong>Email:</strong> info.nomadtrailz@gmail.com</li>
                  <li><strong>Address:</strong> 402 Navneet Plaza Old Palasia Near Apna Sweets Indore</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>12. Updates to Terms</h2>
                <p>
                  NomadTrailz reserves the right to update these terms and conditions at any time. Continued use of our services after changes constitutes acceptance of the new terms.
                </p>
              </div>

              <div className="policy-footer">
                <p>
                  <strong>Last updated:</strong> 01/08/2025
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default TermsConditions;
